import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../common/Button.js';

/**
 * Approve / Reject confirmation modal with optional remarks text input.
 */
const OgclReviewConfirmModal = ({
  open,
  action = 'approve', // 'approve' | 'reject'
  requestId,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [remarks, setRemarks] = useState('');
  const isApprove = action === 'approve';

  useEffect(() => {
    if (open) setRemarks('');
  }, [open, action, requestId]);

  if (!open) return null;

  const title = isApprove ? 'Approve ITCL Request' : 'Reject ITCL Request';
  const confirmLabel = isApprove ? 'Approve' : 'Reject';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => !loading && onClose?.()}
      role="presentation"
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-2xl shadow-xl p-4 sm:p-5 max-h-[90vh] overflow-y-auto min-w-0"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ogcl-review-modal-title"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 id="ogcl-review-modal-title" className="text-base sm:text-lg font-semibold text-secondary">
              {title}
            </h3>
            {requestId ? (
              <p className="text-xs sm:text-sm text-gray-500 mt-1 break-all">{requestId}</p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          {isApprove
            ? 'Approve this ITCL request so the destination hub can unload and dispatch these shipments.'
            : 'Reject this ITCL request. Shipments will return to booked status for the requester.'}
        </p>

        <label className="block text-sm font-medium text-secondary mb-1.5">
          {isApprove ? 'Approve remark' : 'Reject remark'}
          <span className="text-gray-400 font-normal"> (optional)</span>
        </label>
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={3}
          disabled={loading}
          placeholder={isApprove ? 'Enter approve remark…' : 'Enter reject remark…'}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm resize-y min-h-[88px] focus:outline-none focus:border-blue-400"
        />

        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            className="w-full sm:w-auto"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={isApprove ? 'primary' : 'danger'}
            loading={loading}
            disabled={loading}
            className="w-full sm:w-auto"
            onClick={() => onConfirm?.(remarks.trim())}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OgclReviewConfirmModal;
